import { useEffect, useState, useRef, useContext, useMemo } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import Chatbot from "../../components/Chatbot";
import { useAuth } from "../../context/AuthContext";
import { getTranslation } from "../../utils/translations";
import "../../styles/HospitalityHome.css";

// ⭐ Paid provider card styles
const paidCardStyle = {
  border: "3px solid gold",
  background: "linear-gradient(180deg, #FFF9E6 0%, #ffffff 100%)",
  boxShadow: "0 4px 12px rgba(255, 215, 0, 0.35)",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
};

const pulsateAnimation = {
  animation: "pulsate 2s ease-in-out infinite",
};

const hoverGlowStyle = {
  transform: "translateY(-4px)",
  boxShadow: "0 8px 20px rgba(255, 215, 0, 0.6)",
};

const badgePulsate = {
  animation: "badgePulsate 1.5s ease-in-out infinite",
};

const globalStyles = `
@keyframes pulsate {
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
}

@keyframes badgePulsate {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
}
`;

export default function HospitalityHome() {
  const navigate = useNavigate();
  const categoriesRef = useRef(null);

  const { user, token } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = getTranslation(lang);

  const [serviceInput, setServiceInput] = useState("");
  const [locationInput, setLocationInput] = useState("");
  const [aiQuery, setAiQuery] = useState("");
  const [distance, setDistance] = useState("");
  const [coords, setCoords] = useState(null);
  const [allProviders, setAllProviders] = useState([]);
  const [ads, setAds] = useState([]);
  const [validity, setValidity] = useState([]);
  const [ratingsData, setRatingsData] = useState({});
  const [hoveredId, setHoveredId] = useState(null);


  const [facilityName, setFacilityName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [adFile, setAdFile] = useState(null);
  const [showAdForm, setShowAdForm] = useState(false);

  const [isSubmittingAd, setIsSubmittingAd] = useState(false);

  const [notice, setNotice] = useState("");

  const showNotice = (message) => {
    setNotice(message);
    setTimeout(() => setNotice(""), 2500);
  };

  const AD_PRICE = 25;
  const IBAN = "BE67 9673 4428 4587";
  const ACCOUNT_NAME = "Asnakew Bewketu Belete";

  const MAX_VIDEO_DURATION = 120; // seconds
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB


  const singularCategory = {
    Cafes: "Cafe",
    Restaurants: "Restaurant",
    Bars: "Bar",
    Clubs: "Club",
    Hotels: "Hotel",
    Events: "Event",
  };


  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = globalStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);


  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCoords(null)
    );
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await fetch(`/api/providers?lang=${lang}`);
        const data = await res.json();

        if (Array.isArray(data)) {
          setAllProviders(normalizeProviders(data));
        }
      } catch (err) {
        console.error("Failed to load providers", err);
      }
    };

    fetchProviders();
  }, [ratingsData, lang]); // ✅ add lang here



  useEffect(() => {
    fetch(`/api/comments`)
      .then((res) => res.json())
      .then((data) => setRatingsData(data))
      .catch(() => setRatingsData({}));
  }, []);

  useEffect(() => {
    fetch(`/api/ads/paid_providers_ads`)
      .then((res) => res.json())
      .then((data) => setAds(Array.isArray(data) ? data : []))
      .catch(() => setAds([]));
  }, []);

  useEffect(() => {
    if (categoriesRef.current && window.innerWidth <= 768) {
      const firstCard = categoriesRef.current.querySelector("div");
      if (firstCard) {
        const containerWidth = categoriesRef.current.offsetWidth;
        const cardWidth = firstCard.offsetWidth;
        const scrollPos = cardWidth / 2 - containerWidth / 2;
        categoriesRef.current.scrollLeft = scrollPos;
      }
    }
  }, []);

  const distanceBetween = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  };


  const isBranchOpenNow = (branch) => {
    if (branch.forced_closed) return false;

    const now = new Date();
    const day = now.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();

    const open = branch[`${day}_open`];
    const close = branch[`${day}_close`];

    if (!open || !close) return false;

    const [openH, openM] = String(open).split(":").map(Number);
    const [closeH, closeM] = String(close).split(":").map(Number);

    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    return closeMinutes < openMinutes
      ? nowMinutes >= openMinutes || nowMinutes <= closeMinutes
      : nowMinutes >= openMinutes && nowMinutes <= closeMinutes;
  };


  const normalizeKeyword = (word) => {
    const keywordGroups = t.search_keywords || {};

    for (const [canonical, variants] of Object.entries(keywordGroups)) {
      if (
        Array.isArray(variants) &&
        variants.some((variant) => {
          const v = variant.toLowerCase();
          return word === v || word.startsWith(v) || v.startsWith(word);
        })
      ) {
        return canonical;
      }
    }

    return word;
  };


  const matchesAiQuery = (provider, query) => {
    if (!query.trim()) return true;

    const keywords = query
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => normalizeKeyword(word));

    const wantsOpen = keywords.includes("open");

    const searchableText = [
      provider.name,
      provider.category,
      provider.why_choose,
      ...(provider.name_translations ? Object.values(provider.name_translations) : []),
      ...(provider.why_choose_translations ? Object.values(provider.why_choose_translations) : []),

      ...(provider.branches || []).flatMap((b) => [
        b.branch_name,
        b.address,
        b.services,
        ...(b.branch_name_translations ? Object.values(b.branch_name_translations) : []),
        ...(b.address_translations ? Object.values(b.address_translations) : []),
        ...(b.services_translations ? Object.values(b.services_translations) : []),
      ]),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const textKeywords = keywords.filter((word) => word !== "open");

    const textMatch = textKeywords.every((word) => searchableText.includes(word));
    const openMatch =
      !wantsOpen || (provider.branches || []).some((b) => isBranchOpenNow(b));

    return textMatch && openMatch;
  };


  const normalizeProviders = (data) => {
    return data
      .filter((p) => p.paid_plan !== false) // keep providers with plan or id
      .map((p) => {
        let lat = p.lat;
        let lng = p.lng;

        if (p.branches && p.branches.length > 0) {
          const firstBranch = p.branches[0];
          lat = firstBranch.lat || lat;
          lng = firstBranch.lng || lng;
        }

        const ratingEntry = ratingsData[p.id];
        const averageRating = ratingEntry ? ratingEntry.averageRating : null;

        return {
          ...p,
          providerId: p.id,
          providerName: p.name,
          lat,
          lng,
          averageRating,
          membership_plan: p.membership_plan?.toLowerCase() || "basic",
        };
      });
  };


  const isProMember = (provider) => provider.membership_plan === "pro";
  const isPremiumPlan = (provider) => provider.membership_plan === "premium";

  const getPriority = (providerId) => {
    const entry = validity.find((v) => v.id === providerId && v.active);
    return entry ? entry.priority || 0 : 0;
  };

  const premiumAdProviderIds = new Set(ads.map((ad) => ad.id));
  const premiumValidProviderIds = new Set(validity.filter((v) => v.active).map((v) => v.id));
  const isEligibleForPremiumAds = (providerId) =>
    premiumAdProviderIds.has(providerId) || premiumValidProviderIds.has(providerId);

  // ✅ UPDATED SERVICE FILTER: matches provider if ANY branch has the service
  const filteredProviders = allProviders
    // ✅ ONLY PREMIUM PROVIDERS ON HOMEPAGE
    .filter((p) => p.membership_plan === "premium")

    .filter((p) => {
      const serviceMatch =
        !serviceInput ||
        (Array.isArray(p.branches) &&
          p.branches.some((b) =>
            b.services?.toLowerCase().includes(serviceInput.toLowerCase())
          )) ||
        p.category?.toLowerCase().includes(serviceInput.toLowerCase());

      const locationMatch =
        !locationInput ||
        (Array.isArray(p.branches) &&
          p.branches.some((b) =>
            b.address?.toLowerCase().includes(locationInput.toLowerCase())
          ));

      const aiMatch = matchesAiQuery(p, aiQuery);

      let distanceMatch = true;
      if (distance && coords && p.lat && p.lng) {
        distanceMatch =
          distanceBetween(coords.lat, coords.lng, Number(p.lat), Number(p.lng)) <=
          Number(distance);
      }

      return serviceMatch && locationMatch && aiMatch && distanceMatch;
    })
    .map((p) => ({ ...p, priority: getPriority(p.id) }))
    // Premium only → priority still matters
    .sort((a, b) => b.priority - a.priority);

  const hospitalityCategories = [
    { name: "Cafes", emoji: "☕" },
    { name: "Restaurants", emoji: "🍽️" },
    { name: "Bars", emoji: "🍹" },
    { name: "Clubs", emoji: "🎶💃" },
    { name: "Hotels", emoji: "🏨🛏️" },
    // { name: "Events", emoji: "🎉🎤" },
  ];


  const aiSuggestionChips = [
    "open bar",
    "restaurant addis abeba",
    "open addis abeba",
    "cafe bole",
    "open restaurant",
    "club addis abeba",
  ];

  const groupedPremiumProviders = useMemo(() => {
    return hospitalityCategories.reduce((acc, cat) => {
      acc[cat.name] = filteredProviders.filter(
        (p) =>
          p.category === cat.name ||
          p.category === singularCategory[cat.name]
      );
      return acc;
    }, {});
  }, [filteredProviders]);


  const clearFilters = () => {
    setServiceInput("");
    setAiQuery("");
    setLocationInput("");
    setDistance("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const membershipPlans = [
    { key: "basic", ...t.membership.basic },
    { key: "pro", ...t.membership.pro },
    { key: "premium", ...t.membership.premium },
  ];

  const goToCategory = (categoryName) => navigate(`/category/${categoryName}`);

  const getProviderName = (adId) => {
    const provider = allProviders.find((p) => p.id === adId);
    return provider ? provider.name : "";
  };

  return (
    <main style={{ flex: 1, padding: 20, display: "flex", justifyContent: "center" }}>
      {notice && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#ffffff",
            color: "#111",
            padding: "12px 16px",
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            zIndex: 9999,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {notice}
        </div>
      )}
      <div style={{ background: "#0D3B66", padding: 20, borderRadius: 10, width: "100%", maxWidth: 1200 }}>
        {/* HEADINGS */}
        <h1 style={{ color: "#fff", marginBottom: 20 }}>{t.hero_title}</h1>
        <p style={{ color: "#FF7043", fontSize: "clamp(1rem, 2.5vw, 1.3rem)", marginBottom: 20 }}>
          {t.hero_subtitle}
        </p>

        <div style={{ display: "flex", justifyContent: "center", gap: "15px", flexWrap: "wrap" }}>
          {[t.explore_places, t.list_business].map((text) => (
            <button
              key={text}
              style={{
                backgroundColor: "#007BFF",
                color: "#fff",
                padding: "12px 25px",
                borderRadius: 8,
                fontSize: "1.2rem",
                cursor: "pointer",
                border: "none",
                minWidth: "180px",
                fontWeight: "bold",
                marginBottom: 20,
                transition: "all 0.2s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = 0.8)}
              onMouseOut={(e) => (e.currentTarget.style.opacity = 1)}
            >
              {text}
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            marginBottom: 25,
            alignItems: "flex-start",
          }}
        >
          <input
            className="responsive-filter-input"
            placeholder={t.service_placeholder}
            value={serviceInput}
            onChange={(e) => setServiceInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              padding: 10,
              borderRadius: 6,
              flex: "1 1 220px",
              minWidth: 220,
              width: "100%",
              boxSizing: "border-box",
            }}
          />

          <div
            style={{
              flex: "2 1 320px",
              minWidth: 260,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <input
              className="responsive-filter-input"
              placeholder={t.ai_placeholder}
              value={aiQuery}
              onChange={(e) => setAiQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              style={{
                padding: 10,
                borderRadius: 6,
                width: "100%",
                boxSizing: "border-box",
              }}
            />
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 8,
              }}
            >
              {aiSuggestionChips.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setAiQuery(suggestion)}
                  style={{
                    background: "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.25)",
                    borderRadius: 999,
                    padding: "6px 10px",
                    fontSize: 12,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.22)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.12)";
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>

          <input
            className="responsive-filter-input"
            placeholder={t.location_placeholder}
            value={locationInput}
            onChange={(e) => setLocationInput(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              padding: 10,
              borderRadius: 6,
              flex: "1 1 220px",
              minWidth: 220,
              width: "100%",
              boxSizing: "border-box",
            }}
          />

          <input
            className="responsive-filter-input"
            type="number"
            placeholder="e.g. 5 km"
            value={distance}
            onChange={(e) => setDistance(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              padding: 10,
              borderRadius: 6,
              flex: "0 1 120px",
              minWidth: 120,
              width: 120,
              boxSizing: "border-box",
            }}
          />
        </div>


        <div
          style={{
            color: "#fff",
            marginBottom: 20,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ fontSize: 14, opacity: 0.9 }}>
            {filteredProviders.length} {t.results_found}
          </div>

          <button
            onClick={clearFilters}
            style={{
              padding: "8px 14px",
              borderRadius: 8,
              border: "none",
              background: "#FF7043",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: 14,
            }}
          >
            {t.clear_filters}
          </button>
        </div>


        {/* CATEGORIES */}
        <div
          className="categories-scroll"
          ref={categoriesRef}
          style={{
            display: "flex",
            gap: 16,
            overflowX: "auto",
            paddingBottom: 10,
            marginBottom: 25,
            WebkitOverflowScrolling: "touch",
          }}
        >
          {hospitalityCategories.map((cat) => (
            <div
              key={cat.name}
              onClick={() => goToCategory(cat.name)}
              style={{
                background: "#fff",
                padding: 16,
                borderRadius: 12,
                cursor: "pointer",
                textAlign: "center",
                minWidth: 120,
                maxWidth: 140,
                flexShrink: 0,
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 6px rgba(0,0,0,0.1)"; }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: 8 }}>{cat.emoji}</div>
              <div style={{ fontWeight: 500, fontSize: "1rem" }}>
                {t.home_categories?.[cat.name] || cat.name}
              </div>
            </div>
          ))}
        </div>


        {/* UPLOAD AD VIDEO BUTTON WITH SLIDE ANIMATION */}
        <div
          style={{
            marginBottom: 30,
            width: "100%",
            maxWidth: 600,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              fontWeight: "bold",
              fontSize: 16,
              marginBottom: 10,
              color: "#FFD700",
              textShadow: "1px 1px 3px rgba(0,0,0,0.7)",
            }}
          >
            {t.advertise_title}
          </div>

          {/* Wrap button + form in fragment to fix JSX adjacent element error */}
          <>
            {/* Upload button always visible */}
            <button
              onClick={() => {
                if (!user) {
                  showNotice(t.ads_form.login_required_upload);
                } else {
                  setShowAdForm((prev) => !prev);
                }
              }}
              style={{
                width: "100%",
                padding: "12px 20px",
                background: "#007BFF",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: "1rem",
                fontWeight: "bold",
                transition: "background 0.3s ease",
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = "#0056b3")}
              onMouseOut={(e) => (e.currentTarget.style.background = "#007BFF")}
            >
              {showAdForm ? t.close_upload : t.upload_ad}
            </button>

            {/* Form only visible for logged-in users */}
            {user && (
              <div
                style={{
                  maxHeight: showAdForm ? 1000 : 0,
                  overflow: "hidden",
                  transition: "max-height 0.5s ease, opacity 0.5s ease",
                  opacity: showAdForm ? 1 : 0,
                }}
              >
                <div
                  style={{
                    marginTop: 15,
                    padding: 20,
                    background: "#f0f8ff",
                    borderRadius: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <form
                    onSubmit={async (e) => {
                      e.preventDefault();

                      if (isSubmittingAd) return;
                      setIsSubmittingAd(true);

                      if (!token) {
                        setIsSubmittingAd(false);
                        return showNotice(t.ads_form.login_required_submit);
                      }

                      if (!adFile || !startDate || !endDate) {
                        setIsSubmittingAd(false);
                        return showNotice(t.ads_form.all_fields_required);
                      }

                      const formData = new FormData();
                      formData.append("provider_id", user.id);
                      formData.append("media_type", "video");
                      formData.append("start_date", startDate);
                      formData.append("end_date", endDate);
                      formData.append("facility_name", facilityName);

                      const videoFile = new File(
                        [adFile],
                        `${facilityName.replace(/\s+/g, "_")}-${user.id}-${Date.now()}.${adFile.name.split(".").pop()
                        }`,
                        { type: adFile.type }
                      );
                      formData.append("video", videoFile);

                      try {
                        const res = await fetch(`/api/ads/upload`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });

                        if (!res.ok) {
                          const errData = await res.json();
                          throw new Error(errData.message || t.ads_form.upload_failed);
                        }

                        showNotice(t.ads_form.success);
                        setAdFile(null);
                        setFacilityName("");
                        setStartDate("");
                        setEndDate("");
                        setShowAdForm(false);
                        setIsSubmittingAd(false);

                        const adsRes = await fetch(`/api/ads/paid_providers_ads`);
                        const adsData = await adsRes.json();
                        setAds(Array.isArray(adsData) ? adsData : []);
                      } catch (err) {
                        console.error(err);
                        showNotice(`${t.ads_form.upload_failed}: ${err.message}`);
                        setIsSubmittingAd(false);
                      }
                    }}
                    style={{ display: "flex", flexDirection: "column", gap: 12 }}
                  >
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                        style={{
                          padding: 10,
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          flex: 1,
                          minWidth: 120,
                        }}
                      />
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                        style={{
                          padding: 10,
                          borderRadius: 6,
                          border: "1px solid #ccc",
                          flex: 1,
                          minWidth: 120,
                        }}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder={t.ads_form.facility_name_placeholder}
                      value={facilityName}
                      onChange={(e) => setFacilityName(e.target.value)}
                      required
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        border: "1px solid #ccc",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <label
                        htmlFor="videoUpload"
                        style={{
                          padding: "10px 15px",
                          background: "#007BFF",
                          color: "#fff",
                          borderRadius: 6,
                          cursor: "pointer",
                          textAlign: "center",
                          fontWeight: "bold",
                        }}
                      >
                        {t.ads_form.choose_file}
                      </label>

                      <input
                        id="videoUpload"
                        type="file"
                        accept="video/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;

                          if (file.size > MAX_VIDEO_SIZE) {
                            showNotice("Video must be smaller than 50MB");
                            return;
                          }

                          const video = document.createElement("video");
                          video.preload = "metadata";

                          video.onloadedmetadata = () => {
                            window.URL.revokeObjectURL(video.src);

                            if (video.duration > MAX_VIDEO_DURATION) {
                              showNotice("Video must be 2 minutes or less.");
                              return;
                            }

                            setAdFile(file);
                          };

                          video.src = URL.createObjectURL(file);
                        }}
                        required
                        style={{ display: "none" }}
                      />

                      <div style={{ fontSize: 14, color: "#555" }}>
                        {adFile ? adFile.name : t.ads_form.no_file_chosen}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "none", // hidden for now, easy to restore later
                        backgroundColor: "#1e293b",
                        border: "1px solid #334155",
                        borderRadius: "10px",
                        padding: "12px",
                        color: "#ffffff",
                        lineHeight: 1.6,
                        marginTop: 10
                      }}
                    >
                      <p style={{ margin: 0, fontWeight: "bold", color: "#facc15" }}>
                        {t.ads_form.payment_information}
                      </p>

                      <p style={{ margin: "8px 0 0 0", color: "#ffffff" }}>
                        {t.ads_form.payment_instruction}
                      </p>

                      <p style={{ margin: "8px 0 0 0", color: "#ffffff" }}>
                        {t.ads_form.ad_video_one_month}
                      </p>

                      <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                        <strong>{t.ads_form.ad_fee_label}:</strong> €25 {t.ads_form.per_month}
                      </p>

                      <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                        <strong>{t.ads_form.iban_label}:</strong> BE67 9673 4428 4587
                      </p>

                      <p style={{ margin: "4px 0 0 0", color: "#ffffff" }}>
                        <strong>{t.ads_form.account_name_label}:</strong> Asnakew Bewketu Belete
                      </p>

                      <p style={{ margin: "6px 0 0 0", color: "#ffffff", fontSize: 13 }}>
                        {t.ads_form.video_limit_note}
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmittingAd}
                      style={{
                        padding: "10px 15px",
                        background: isSubmittingAd ? "#6c757d" : "#28a745",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: isSubmittingAd ? "not-allowed" : "pointer",
                        width: "100%",
                        fontWeight: "bold",
                        fontSize: 16,
                      }}
                    >
                      {isSubmittingAd ? t.ads_form.submitting : t.ads_form.submit_ad}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </>
        </div>

        {/* PREMIUM ADS */}
        {ads.length > 0 && (
          <div style={{ marginBottom: 30 }}>
            <h3 style={{ color: " #00CED1", fontSize: 25, marginBottom: 15, fontWeight: "bold" }}>{t.sponsored_ads}</h3>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
              {ads.map((ad) => {
                if (!ad.file_url) return null;
                const provider = allProviders.find((p) => p.id === ad.provider_id); // match provider by provider_id
                const providerName = provider ? provider.name : "";
                const verified = provider?.verified;

                return ad.media_type === "video" ? (
                  <div
                    key={ad.id}
                    style={{
                      position: "relative",
                      width: 280,
                      height: 180,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "3px solid #00CED1",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      //alignItems: "center",
                    }}
                  >
                    {/* Facility name overlay with verified badge and pulsate */}
                    {ad.facility_name && (
                      <div
                        style={{
                          position: "absolute",
                          top: 2,
                          right: -38,
                          background: "rgba(255, 215, 0, 0.6)",
                          color: "#28a745",
                          padding: "2px 6px",
                          borderRadius: 8,
                          fontWeight: "bold",
                          fontSize: 16,
                          textAlign: "center",
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 0,
                          maxWidth: "90%",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          textShadow: "2px 2px 2px rgba(0,0,0,0.7)",
                          animation: "facilityPulsate 1.8s ease-in-out infinite",
                        }}
                      >
                        <span style={{ color: "#fff" }}>{ad.facility_name}</span>
                        <span style={{ color: "#FFD700", marginLeft: 4 }}>✅</span>
                      </div>
                    )}

                    <video
                      src={ad.file_url}
                      controls
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />

                    {/* Sponsored badge */}
                    <div
                      style={{
                        position: "absolute",
                        top: 2,
                        left: 1,
                        background: "#00CED1",
                        color: "#000",
                        padding: "4px 6px",
                        borderRadius: 6,
                        fontWeight: "bold",
                        fontSize: 12,
                      }}
                    >
                      {t.ads_form.sponsored_badge}
                    </div>
                  </div>
                ) : (
                  <div
                    key={ad.id}
                    style={{
                      position: "relative",
                      width: 280,
                      height: 180,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "3px solid gold",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "stretch",
                    }}
                  >
                    {providerName && (
                      <div style={{ marginBottom: 4, fontWeight: "bold", color: "#fff", fontSize: 14, textShadow: "1px 1px 4px rgba(0,0,0,0.7)" }}>
                        {providerName} {verified && "✅"}
                      </div>
                    )}
                    <img src={ad.file_url} alt={ad.caption} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    <div style={{ position: "absolute", top: 2, left: 3, background: "gold", color: "#000", padding: "4px 6px", borderRadius: 6, fontWeight: "bold", fontSize: 12 }}>{t.ads_form.premium_badge}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* FEATURED PROVIDERS */}
        {filteredProviders.length > 0 && (
          <>
            <h3
              style={{
                color: "#FFD700",
                marginBottom: 20,
                fontSize: 25,
                fontWeight: "bold",
              }}
            >
              {t.premium_providers}
            </h3>

            {hospitalityCategories.map((cat) => {
              const providersInCategory = groupedPremiumProviders[cat.name] || [];

              if (providersInCategory.length === 0) return null;

              return (
                <div key={cat.name} style={{ marginBottom: 35 }}>
                  <h4
                    style={{
                      color: "#00CED1",
                      marginBottom: 15,
                      fontSize: 22,
                      fontWeight: "bold",
                      borderBottom: "2px solid rgba(255,255,255,0.2)",
                      paddingBottom: 6,
                    }}
                  >
                    <span style={{ marginRight: 8 }}>{cat.emoji}</span>
                    {t.home_categories?.[cat.name] || cat.name}
                  </h4>

                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      overflowX: "auto",
                      paddingBottom: 10,
                      scrollBehavior: "smooth",
                      WebkitOverflowScrolling: "touch",
                      alignItems: "stretch",
                    }}
                  >
                    {providersInCategory.map((p) => {
                      const distanceKm =
                        coords && p.lat && p.lng
                          ? distanceBetween(
                            coords.lat,
                            coords.lng,
                            Number(p.lat),
                            Number(p.lng)
                          )
                          : null;

                      const premium = isPremiumPlan(p);

                      return (
                        <Link
                          key={p.id}
                          to={`/provider/${p.id}`}
                          onMouseEnter={() => setHoveredId(p.id)}
                          onMouseLeave={() => setHoveredId(null)}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            textDecoration: "none",
                            color: "#0f172a",
                            width: 230,
                            minHeight: 230,
                            padding: 12,
                            flexShrink: 0,
                            borderRadius: 12,
                            transition: "transform 0.3s ease, box-shadow 0.3s ease",
                            ...(premium
                              ? {
                                ...paidCardStyle,
                                ...pulsateAnimation,
                                ...(hoveredId === p.id ? hoverGlowStyle : {}),
                              }
                              : {
                                border: "2px solid #28a745",
                                background: "#fff",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                ...(hoveredId === p.id
                                  ? {
                                    transform: "translateY(-3px)",
                                    boxShadow:
                                      "0 6px 16px rgba(0,0,0,0.15)",
                                  }
                                  : {}),
                              }),
                          }}
                        >
                          <div>
                            {p.image_url && (
                              <img
                                src={p.image_url}
                                alt={p.name}
                                style={{
                                  width: "100%",
                                  height: 90,
                                  objectFit: "cover",
                                  borderRadius: 8,
                                  marginBottom: 10,
                                }}
                              />
                            )}
                            <div style={{ position: "relative" }}>
                              <div
                                style={{
                                  position: "absolute",
                                  top: -10,
                                  right: -10,
                                  background: premium ? "#FFD700" : "#28a745",
                                  color: premium ? "#856404" : "#fff",
                                  padding: "4px 8px",
                                  borderRadius: 6,
                                  fontWeight: "bold",
                                  fontSize: 12,
                                  ...(premium ? badgePulsate : {}),
                                }}
                              >
                                {premium
                                  ? t.ads_form.premium_label
                                  : t.ads_form.pro_label}
                              </div>

                              <h4
                                style={{
                                  margin: 0,
                                  fontWeight: "bold",
                                  fontSize: 16,
                                }}
                              >
                                {p.name} {p.verified && "✅"}
                              </h4>
                            </div>

                            {p.averageRating && (
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: "bold",
                                  marginTop: 4,
                                }}
                              >
                                ⭐ {p.averageRating}
                              </div>
                            )}

                            <div
                              style={{
                                fontSize: 13,
                                color: "#777",
                                marginTop: 4,
                              }}
                            >
                              {p.description}
                            </div>

                            {Array.isArray(p.branches) && p.branches.length > 0 && (
                              <div
                                style={{
                                  fontSize: 13,
                                  color: "#777",
                                  marginTop: 6,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 4,
                                }}
                              >
                                {p.branches.map((branch) => (
                                  <div key={branch.id}>
                                    <span
                                      style={{
                                        color: "#FF7043",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {branch.branch_name}:
                                    </span>{" "}
                                    {branch.services}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div style={{ marginTop: "auto" }}>
                            {distanceKm !== null && (
                              <div style={{ fontSize: 13, color: "#555" }}>
                                {distanceKm.toFixed(1)} {t.ads_form.km_away}
                              </div>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}



        {/* CITY PARTNER CTA */}
        <div
          style={{
            marginBottom: 30,
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16,
            padding: "28px 22px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            border: "2px solid rgba(255,255,255,0.25)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: "1 1 420px", minWidth: 260 }}>
              <div
                style={{
                  display: "inline-block",
                  background: "#0D3B66",
                  color: "#fff",
                  padding: "6px 12px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: "bold",
                  marginBottom: 12,
                }}
              >
                {t.city_partner.badge}
              </div>

              <h3
                style={{
                  margin: "0 0 10px 0",
                  fontSize: "clamp(1.4rem, 3vw, 2rem)",
                  color: "#0f172a",
                  fontWeight: "800",
                }}
              >
                {t.city_partner.title}
              </h3>

              <p
                style={{
                  margin: 0,
                  color: "#334155",
                  fontSize: 16,
                  lineHeight: 1.7,
                  maxWidth: 700,
                }}
              >
                {t.city_partner.description}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => navigate("/city-partner")}
                style={{
                  background: "#FF7043",
                  color: "#fff",
                  padding: "12px 20px",
                  borderRadius: 10,
                  border: "none",
                  fontWeight: "bold",
                  fontSize: 16,
                  cursor: "pointer",
                  boxShadow: "0 6px 18px rgba(255,112,67,0.35)",
                }}
              >
                {t.city_partner.apply_button}
              </button>

              <button
                onClick={() => navigate("/city-partner")}
                style={{
                  background: "#0D3B66",
                  color: "#fff",
                  padding: "12px 20px",
                  borderRadius: 10,
                  border: "none",
                  fontWeight: "bold",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {t.city_partner.learn_more_button}
              </button>
            </div>
          </div>
        </div>


        {/* MEMBERSHIP PLANS */}
        <div style={{ marginBottom: 30 }}>
          <h3 style={{ color: "#28A745", marginBottom: 15, fontSize: 25, fontWeight: "bold" }}>{t.membership_plans}</h3>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center" }}>
            {membershipPlans.map((plan) => {
              let borderColor = plan.name === "Pro" ? "green" : plan.name === "Premium" ? "gold" : "#ccc";
              return (
                <div
                  key={plan.name}
                  style={{
                    background: "#fff",
                    padding: 20,
                    fontSize: 18,
                    borderRadius: 12,
                    width: "100%",
                    maxWidth: 280,
                    position: "relative",
                    border: `3px solid ${borderColor}`,
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.15)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"; }}
                >
                  <div style={{ position: "absolute", top: 20, right: 20, fontWeight: "bold", fontSize: 16, background: "#f0f0f0", color: "#007BFF", padding: "4px 8px", borderRadius: 6 }}>
                    {plan.price}
                  </div>
                  <h4>
                    {plan.name}
                    {plan.isPaid && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 13,
                          background: "#28a745",
                          color: "#fff",
                          padding: "2px 6px",
                          borderRadius: 4
                        }}
                      >
                        {t.membership.paid_badge}
                      </span>
                    )}
                  </h4>
                  {plan.planType && <div style={{ fontSize: 15, color: "#007BFF", marginBottom: 6, fontWeight: "bold" }}>{plan.planType}</div>}
                  <ul>
                    {plan.features.map((feature, idx) => {
                      const [main, sub] = feature.split(" - ");
                      return (
                        <li key={idx}>
                          {main}
                          {sub && <div style={{ fontSize: 14, color: "#555", marginTop: 7 }}>- {sub}</div>}
                        </li>
                      );
                    })}
                  </ul>
                  <button style={{ marginTop: 10, padding: "8px 12px", background: "#FF7043", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}>
                    {t.membership.choose_plan}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* CHATBOT */}
        <Chatbot />
      </div>
    </main>
  );
}
